function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import debounce from 'lodash.debounce';
import React, { useCallback, useMemo } from 'react';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';

/**
 * This component renders an asynchronous selector for options.
 * 
 * @param {function(string): void} fetchAndOptionsSetter - An API call to search for new options, receives the search value as a string. 
 * it should be an "async" function that returns "an Array of options": [{value: 'some value', label: 'some value label}, ...]
 * It will also be debounced to the debounced time by the default or specified value
 * 
 * @param {number} [searchForMoreOptionsMessage] - Optional. a message for when it should warn the user that 
 * they should type in to be able to view more options
 * 
 * @param {number} [searchDebounceTime] - Optional. The number of milliseconds to wait before triggering the search. Default is 1000.
 * @param {boolean} [isMulti] - Optional (default: false). for multi select pass true
 * @param {boolean} [searchForMoreOptions] - Optional (default: false). if ture will use the async selector.
 * @param {boolean} [unselectOptionAfterSelect] - Optional (default: false). if set to true you should pass "selectedOption" too. 
 * you should handle selectedOption with onChange property or the option will not be selected on Change
 * 
 * @param {object} [selectedOption] - Pass if 'unselectOptionAfterSelect' is passed as true. 
 * This should be a state that you can set to null in order to manually unselect the component.
 * 
 * @param {function(string): void} onChange - Receives selected option. if isMulti then will receive an array of objects
 * otherwise will receive an object. object will have a value and a label.
 * 
 * @param {array} defaultOptions - Array of objects for default options: [{value: 'some value', label: 'some value label}, ...]
 * 
 * @returns {ReactNode} A React element that renders the asynchronous selector.
 */

const ReactSelector = _ref => {
  let {
    searchDebounceTime = 1000,
    selectedOption = null,
    //optional
    unselectOptionAfterSelect = false,
    onChange,
    isMulti = false,
    defaultOptions = [],
    searchForMoreOptions = false,
    fetchAndOptionsSetter,
    searchForMoreOptionsMessage = 'There are more options that you can see. Type in to view more'
  } = _ref;
  const remoteSearch = useCallback((inputValue, callback) => {
    (async () => {
      const res = await fetchAndOptionsSetter(inputValue);
      callback(res);
    })();
  }, [fetchAndOptionsSetter]);
  const loadOptions = useMemo(() => debounce(remoteSearch, searchDebounceTime), [remoteSearch, searchDebounceTime]);
  const commonProps = {
    isMulti,
    onChange,
    ...(unselectOptionAfterSelect && {
      value: selectedOption
    }) // Conditionally add value
  };
  if (searchForMoreOptions) {
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", null, searchForMoreOptionsMessage), /*#__PURE__*/React.createElement(AsyncSelect, _extends({
      loadOptions: loadOptions // Function to load options based on user input
      ,
      defaultOptions: defaultOptions // Show some default options initially if needed
      ,
      placeholder: "Search for options..."
    }, commonProps)));
  } else {
    return /*#__PURE__*/React.createElement(Select, _extends({
      options: defaultOptions
    }, commonProps));
  }
};
export default ReactSelector;